import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  Paper,
} from "@mhome/ui";
import { useMediaQuery } from "@mui/material";
import { spacingToPx } from "../lib/utils";
import { useRecoilValue } from "recoil";
import {
  activeScopeState,
  safeAreaInsetsState,
  scopePreferenceState,
} from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { listLocations } from "../service/scope-service";
import { useToast } from "../common/toast";
import Table from "../table/table";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LabelIcon from "@mui/icons-material/Label";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TimelineIcon from "@mui/icons-material/Timeline";
import CircadianDeleteDialog from "./circadian-delete-dialog";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const TAG_OPTIONS = [
  { value: "brightness", label: "Brightness" },
  { value: "colorTemperature", label: "Color Temperature" },
  { value: "colorHue", label: "Color Hue" },
  { value: "temperature", label: "Temperature" },
  { value: "temperatureDifference", label: "Temperature Difference" },
];

// Transition type options for all tags
const TRANSITION_OPTIONS = [
  { value: "l", label: "Steady (Linear)" },
  { value: "ei", label: "Accelerate" },
  { value: "eo", label: "Decelerate" },
  { value: "lg", label: "Smooth (S-curve)" },
];

const CircadianEditDialog = ({
  open,
  onClose,
  widget,
  onSave,
  onDelete,
  isCreate = false,
}) => {
  const { sendAndWaitWs } = useWebSocket();
  const activeScope = useRecoilValue(activeScopeState);
  const scopePreference = useRecoilValue(scopePreferenceState);
  const { errorToast } = useToast();

  // Form state
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [schedules, setSchedules] = useState({});
  const [activeCurve, setActiveCurve] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [editingCurveName, setEditingCurveName] = useState("");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const getComponentBgColor = useComponentBackgroundColor();

  // Validation errors
  const [nameError, setNameError] = useState("");
  const [tagError, setTagError] = useState("");
  const [locationError, setLocationError] = useState("");

  // Delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load locations on mount
  useEffect(() => {
    if (open) {
      fetchLocations();
    }
  }, [open]);

  // Get default schedule based on tag type
  const getDefaultSchedule = (tagType, currentScopePreference = null) => {
    switch (tagType) {
      case "brightness":
        return [
          { time: "SUNRISE", value: "5", transition: "l" },
          { time: "SUNRISE+60min", value: "40", transition: "l" },
          { time: "12:00:00", value: "100", transition: "l" },
          { time: "15:00:00", value: "100", transition: "l" },
          { time: "SUNSET-60min", value: "80", transition: "l" },
          { time: "SUNSET", value: "60", transition: "l" },
          { time: "SUNSET+60min", value: "40", transition: "l" },
          { time: "22:00:00", value: "10", transition: "l" },
        ];
      case "colorTemperature":
        return [
          { time: "SUNRISE", value: "2500", transition: "l" },
          { time: "SUNRISE+60min", value: "4000", transition: "l" },
          { time: "12:00:00", value: "5500", transition: "l" },
          { time: "15:00:00", value: "5500", transition: "l" },
          { time: "SUNSET-60min", value: "4500", transition: "l" },
          { time: "SUNSET", value: "3500", transition: "l" },
          { time: "22:00:00", value: "2500", transition: "l" },
        ];
      case "colorHue":
        // 8am to 6pm, every 10 minutes, alternating 0-359-0-359...
        const schedule = [];
        const startHour = 8;
        const endHour = 22;
        const totalMinutes = (endHour - startHour) * 60; // 600 minutes
        const intervalMinutes = 10;
        const numPoints = Math.floor(totalMinutes / intervalMinutes) + 1; // 61 points

        for (let i = 0; i < numPoints; i++) {
          const minutes = i * intervalMinutes;
          const hour = startHour + Math.floor(minutes / 60);
          const minute = minutes % 60;
          const timeStr = `${String(hour).padStart(2, "0")}:${String(
            minute
          ).padStart(2, "0")}:00`;

          // Alternate between 0 and 359
          const hueValue = i % 2 === 0 ? "0" : "359";

          schedule.push({ time: timeStr, value: hueValue, transition: "l" });
        }

        return schedule;
      case "temperature":
        const useFahrenheit = currentScopePreference?.temperatureUnit === "F";
        if (useFahrenheit) {
          return [
            { time: "SUNRISE-60min", value: "64F", transition: "l" }, // Pre-dawn: coolest (18C)
            { time: "SUNRISE", value: "66F", transition: "l" }, // Sunrise: still cool (19C)
            { time: "08:00:00", value: "70F", transition: "l" }, // Morning: comfortable (21C)
            { time: "12:00:00", value: "77F", transition: "l" }, // Noon: warm (25C)
            { time: "15:00:00", value: "79F", transition: "l" }, // Afternoon: warmest (26C)
            { time: "SUNSET-60min", value: "75F", transition: "l" }, // Evening: cooling down (24C)
            { time: "SUNSET", value: "72F", transition: "l" }, // Sunset: comfortable (22C)
            { time: "SUNSET+60min", value: "68F", transition: "l" }, // Night: cooler (20C)
            { time: "23:00:00", value: "66F", transition: "l" }, // Late night: cool for sleep (19C)
          ];
        } else {
          return [
            { time: "SUNRISE-60min", value: "18C", transition: "l" }, // Pre-dawn: coolest
            { time: "SUNRISE", value: "19C", transition: "l" }, // Sunrise: still cool
            { time: "08:00:00", value: "21C", transition: "l" }, // Morning: comfortable
            { time: "12:00:00", value: "25C", transition: "l" }, // Noon: warm
            { time: "15:00:00", value: "26C", transition: "l" }, // Afternoon: warmest
            { time: "SUNSET-60min", value: "24C", transition: "l" }, // Evening: cooling down
            { time: "SUNSET", value: "22C", transition: "l" }, // Sunset: comfortable
            { time: "SUNSET+60min", value: "20C", transition: "l" }, // Night: cooler
            { time: "23:00:00", value: "19C", transition: "l" }, // Late night: cool for sleep
          ];
        }
      case "temperatureDifference":
        // Temperature difference schedule (relative changes)
        const useFahrenheitForDiff =
          currentScopePreference?.temperatureUnit === "F";

        if (useFahrenheitForDiff) {
          return [
            { time: "SUNRISE-60min", value: "-5F", transition: "l" }, // Pre-dawn: cooler (-3C ≈ -5F)
            { time: "SUNRISE", value: "-4F", transition: "l" }, // Sunrise: slightly cooler (-2C ≈ -4F)
            { time: "08:00:00", value: "0F", transition: "l" }, // Morning: baseline
            { time: "12:00:00", value: "5F", transition: "l" }, // Noon: warmer (3C ≈ 5F)
            { time: "15:00:00", value: "7F", transition: "l" }, // Afternoon: warmest difference (4C ≈ 7F)
            { time: "SUNSET-60min", value: "4F", transition: "l" }, // Evening: moderately warmer (2C ≈ 4F)
            { time: "SUNSET", value: "0F", transition: "l" }, // Sunset: baseline
            { time: "SUNSET+60min", value: "-4F", transition: "l" }, // Night: cooler (-2C ≈ -4F)
            { time: "23:00:00", value: "-5F", transition: "l" }, // Late night: coolest difference (-3C ≈ -5F)
          ];
        } else {
          return [
            { time: "SUNRISE-60min", value: "-3C", transition: "l" }, // Pre-dawn: cooler
            { time: "SUNRISE", value: "-2C", transition: "l" }, // Sunrise: slightly cooler
            { time: "08:00:00", value: "0C", transition: "l" }, // Morning: baseline
            { time: "12:00:00", value: "3C", transition: "l" }, // Noon: warmer
            { time: "15:00:00", value: "4C", transition: "l" }, // Afternoon: warmest difference
            { time: "SUNSET-60min", value: "2C", transition: "l" }, // Evening: moderately warmer
            { time: "SUNSET", value: "0C", transition: "l" }, // Sunset: baseline
            { time: "SUNSET+60min", value: "-2C", transition: "l" }, // Night: cooler
            { time: "23:00:00", value: "-3C", transition: "l" }, // Late night: coolest difference
          ];
        }
      default:
        // Fallback schedule
        return [
          { time: "06:00:00", value: "100", transition: "l" },
          { time: "12:00:00", value: "80", transition: "l" },
          { time: "18:00:00", value: "60", transition: "l" },
          { time: "22:00:00", value: "20", transition: "l" },
        ];
    }
  };

  // Helper function to find matching location by comparing location data
  const findMatchingLocationId = (widgetLocation, locationsList) => {
    if (!widgetLocation || !locationsList.length) return "";

    // Try to match by name first (most reliable for user-created locations)
    let match = locationsList.find((loc) => loc.name === widgetLocation.name);

    // If no name match, try to match by coordinates (for precise location matching)
    if (!match && widgetLocation.latitude && widgetLocation.longitude) {
      match = locationsList.find(
        (loc) =>
          Math.abs(loc.latitude - widgetLocation.latitude) < 0.001 &&
          Math.abs(loc.longitude - widgetLocation.longitude) < 0.001
      );
    }

    // If still no match, try timezone + approximate coordinates
    if (!match && widgetLocation.timezone) {
      match = locationsList.find(
        (loc) =>
          loc.timezone === widgetLocation.timezone &&
          widgetLocation.latitude &&
          widgetLocation.longitude &&
          Math.abs(loc.latitude - widgetLocation.latitude) < 0.01 &&
          Math.abs(loc.longitude - widgetLocation.longitude) < 0.01
      );
    }

    return match ? match.locationId : "";
  };

  // Initialize form data when widget changes
  useEffect(() => {
    if (widget) {
      setName(widget.name || "");
      setTag(widget.tag || "");
      // For existing widgets, find the matching location from the locations list
      const matchingLocationId = findMatchingLocationId(
        widget.location,
        locations
      );
      setSelectedLocation(matchingLocationId);
      setSchedules(widget.schedules);
      setActiveCurve(widget.curve || "");

      // Set current tab to the active curve
      const curveNames = Object.keys(widget.schedules);
      const activeIndex = curveNames.indexOf(widget.curve);
      const tabIndex = activeIndex >= 0 ? activeIndex : 0;
      setCurrentTab(tabIndex);
      setEditingCurveName(curveNames[tabIndex] || "");
    } else if (isCreate) {
      // Default values for new widget
      setName("");
      setTag(""); // Start empty for validation
      setSelectedLocation(""); // Start empty for validation
      setSchedules({}); // Start with empty schedules until tag is selected
      setActiveCurve("");
      setCurrentTab(0);
      setEditingCurveName("");
    }

    // Clear validation errors when widget changes
    setNameError("");
    setTagError("");
    setLocationError("");
  }, [widget, isCreate, locations]);

  // Update schedules when tag changes for new widgets
  useEffect(() => {
    if (isCreate && tag && !widget) {
      const defaultCurveName = "Default";
      const defaultSchedule = getDefaultSchedule(
        tag,
        scopePreference?.[activeScope]
      );
      setSchedules({ [defaultCurveName]: defaultSchedule });
      setActiveCurve(defaultCurveName);
      setCurrentTab(0);
      setEditingCurveName(defaultCurveName);
    }
  }, [tag, isCreate, widget]);

  const fetchLocations = async () => {
    try {
      const response = await listLocations(sendAndWaitWs);
      if (response && response.locations) {
        setLocations(response.locations);
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Failed to fetch locations:", error);
      errorToast("Failed to fetch locations");
      setLocations([]);
    }
  };

  // Helper function to determine dataType based on tag
  const getDataTypeFromTag = (tagValue) => {
    switch (tagValue) {
      case "temperature":
        return "Temperature";
      case "temperatureDifference":
        return "TemperatureDiff";
      default:
        return "Number";
    }
  };

  const handleSave = () => {
    // Clear previous errors
    setNameError("");
    setTagError("");
    setLocationError("");

    let hasErrors = false;

    // Validate name
    if (!name.trim()) {
      setNameError("Name is required");
      hasErrors = true;
    }

    // Validate tag
    if (!tag) {
      setTagError("Tag is required");
      hasErrors = true;
    }

    // Validate location (always required)
    if (!selectedLocation) {
      setLocationError("Location is required");
      hasErrors = true;
    }

    // Validate schedules
    const curveNames = Object.keys(schedules);
    if (curveNames.length === 0) {
      errorToast("At least one schedule curve is required");
      return;
    }

    // Validate active curve
    if (!activeCurve || !schedules[activeCurve]) {
      errorToast("Active curve must be selected");
      return;
    }

    // Validate all schedule points in all curves
    for (const [curveName, schedule] of Object.entries(schedules)) {
      if (!schedule || schedule.length === 0) {
        errorToast(
          `Schedule curve "${curveName}" must have at least one point`
        );
        return;
      }

      for (let i = 0; i < schedule.length; i++) {
        const point = schedule[i];
        if (!point.time || !point.value) {
          errorToast(
            `Schedule point ${i + 1} in curve "${curveName}" is incomplete`
          );
          return;
        }
      }
    }

    // If there are validation errors, don't proceed
    if (hasErrors) {
      return;
    }

    const selectedLocationObj = locations.find(
      (loc) => loc.locationId === selectedLocation
    );

    // Get default values for multiplier and offset based on tag
    const getDefaultOffset = (tagType) => {
      if (tagType === "temperature" || tagType === "temperatureDifference") {
        return `0${scopePreference?.[activeScope]?.temperatureUnit || "C"}`;
      }
      return "0";
    };

    const widgetData = {
      ...(widget?.id && { id: widget.id }),
      scopeId: activeScope,
      name: name.trim(),
      tag: tag,
      location: selectedLocationObj || null,
      schedules: schedules,
      curve: activeCurve,
      dataType: getDataTypeFromTag(tag),
      // Use existing values if editing, or defaults if creating
      multiplier: widget?.multiplier !== undefined ? widget.multiplier : 1,
      offset:
        widget?.offset !== undefined ? widget.offset : getDefaultOffset(tag),
    };

    onSave(widgetData);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (widget?.id && onDelete) {
      onDelete(widget.id);
    }
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleScheduleChange = (newScheduleData) => {
    const curveNames = Object.keys(schedules);
    if (curveNames.length > 0) {
      const currentCurveName = curveNames[currentTab] || curveNames[0];
      setSchedules((prev) => ({
        ...prev,
        [currentCurveName]: newScheduleData,
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Update editing curve name when switching tabs
    const curveNames = Object.keys(schedules);
    const newCurveName = curveNames[newValue] || curveNames[0] || "";
    setEditingCurveName(newCurveName);
  };

  const handleAddCurve = () => {
    const curveNames = Object.keys(schedules);
    let newCurveName = "New Curve";
    let counter = 1;

    // Find a unique name
    while (curveNames.includes(newCurveName)) {
      newCurveName = `New Curve ${counter}`;
      counter++;
    }

    const defaultSchedule = getDefaultSchedule(
      tag,
      scopePreference?.[activeScope]
    );
    setSchedules((prev) => ({
      ...prev,
      [newCurveName]: defaultSchedule,
    }));

    // Switch to the new tab
    const newTabIndex = curveNames.length;
    setCurrentTab(newTabIndex);
    setEditingCurveName(newCurveName);
  };

  const handleDeleteCurve = (curveName) => {
    const curveNames = Object.keys(schedules);
    if (curveNames.length <= 1) {
      errorToast("Cannot delete the last schedule curve");
      return;
    }

    // Cannot delete the active curve - user must change active curve in detail dialog first
    if (activeCurve === curveName) {
      errorToast(
        "Cannot delete the active curve. Please set a different curve as active first."
      );
      return;
    }

    const newSchedules = { ...schedules };
    delete newSchedules[curveName];
    setSchedules(newSchedules);

    // Update current tab
    const curveIndex = curveNames.indexOf(curveName);
    let newTabIndex = currentTab;
    if (currentTab >= curveIndex && currentTab > 0) {
      newTabIndex = currentTab - 1;
      setCurrentTab(newTabIndex);
    }

    // Update editing curve name
    const remainingCurveNames = Object.keys(newSchedules);
    setEditingCurveName(
      remainingCurveNames[newTabIndex] || remainingCurveNames[0] || ""
    );
  };

  const handleCurveNameChange = (oldName, newName) => {
    if (oldName === newName || !newName.trim()) return;

    const curveNames = Object.keys(schedules);
    if (curveNames.includes(newName.trim())) {
      errorToast("Curve name already exists");
      return;
    }

    const newSchedules = { ...schedules };
    newSchedules[newName.trim()] = newSchedules[oldName];
    delete newSchedules[oldName];
    setSchedules(newSchedules);

    // Update active curve if it was renamed
    if (activeCurve === oldName) {
      setActiveCurve(newName.trim());
    }

    // Update editing curve name
    setEditingCurveName(newName.trim());
  };

  const handleNameChange = (value) => {
    setName(value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleTagChange = (value) => {
    setTag(value);
    if (tagError) {
      setTagError("");
    }
  };

  const handleLocationChange = (value) => {
    setSelectedLocation(value);
    if (locationError) {
      setLocationError("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
          marginBottom: isSmallScreen
            ? 0
            : `${safeAreaInsets.bottom}px !important`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        className="flex items-center gap-2"
        style={{
          minHeight: "auto",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{
            flexShrink: 0,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{
            lineHeight: 1.2,
            marginBottom: 0,
          }}
        >
          {isCreate ? "Create Circadian Schedule" : "Edit Circadian Schedule"}
        </Typography>
      </DialogTitle>

      <DialogContent
        style={{
          paddingLeft: spacingToPx(2),
          paddingRight: spacingToPx(2),
          paddingTop: spacingToPx(2),
          paddingBottom: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Scrollable Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingTop: spacingToPx(2),
            paddingBottom: spacingToPx(4),
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(3),
            }}
          >
            {/* Basic Information Card */}
            <Paper
              variant="outlined"
              style={{
                padding: spacingToPx(2),
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                className="flex items-center gap-2"
                style={{ marginBottom: spacingToPx(2) }}
              >
                <LabelIcon fontSize="small" /> Basic Information
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacingToPx(2),
                }}
              >
                {/* Name Field */}
                <div>
                  <Typography
                    variant="caption"
                    style={{
                      marginBottom: spacingToPx(0.5),
                      display: "block",
                      color: "hsl(var(--muted-foreground))",
                      fontWeight: 500,
                    }}
                  >
                    Name{" "}
                    <span style={{ color: "hsl(var(--destructive))" }}>*</span>
                  </Typography>
                  <TextField
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    error={!!nameError}
                    helperText={nameError || "Name for your circadian schedule"}
                    size="small"
                    placeholder="Enter schedule name"
                  />
                </div>

                {/* Tag Selection */}
                <FormControl fullWidth error={!!tagError} size="small">
                  <Typography
                    variant="caption"
                    style={{
                      marginBottom: spacingToPx(0.5),
                      display: "block",
                      color: "hsl(var(--muted-foreground))",
                      fontWeight: 500,
                    }}
                  >
                    Category{" "}
                    <span style={{ color: "hsl(var(--destructive))" }}>*</span>
                  </Typography>
                  <Select
                    value={tag}
                    onChange={(e) => handleTagChange(e.target.value)}
                    disabled={!isCreate} // Can't edit tag after creation
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {TAG_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {tagError ? (
                    <Typography
                      variant="caption"
                      color="error"
                      style={{ marginTop: spacingToPx(0.5), display: "block" }}
                    >
                      {tagError}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      style={{ marginTop: spacingToPx(0.5), display: "block" }}
                    >
                      What&apos;s the circadian schedule for?
                    </Typography>
                  )}
                </FormControl>

                {/* Location Selection */}
                <FormControl fullWidth error={!!locationError} size="small">
                  <Typography
                    variant="caption"
                    style={{
                      marginBottom: spacingToPx(0.5),
                      display: "block",
                      color: "hsl(var(--muted-foreground))",
                      fontWeight: 500,
                    }}
                  >
                    Location{" "}
                    <span style={{ color: "hsl(var(--destructive))" }}>*</span>
                  </Typography>
                  <Select
                    value={selectedLocation}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationOnIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {locations.map((location) => (
                      <MenuItem
                        key={location.locationId}
                        value={location.locationId}
                      >
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {locationError ? (
                    <Typography
                      variant="caption"
                      color="error"
                      style={{ marginTop: spacingToPx(0.5), display: "block" }}
                    >
                      {locationError}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      style={{ marginTop: spacingToPx(0.5), display: "block" }}
                    >
                      Used for circadian schedule calculations
                    </Typography>
                  )}
                </FormControl>
              </div>
            </Paper>

            {/* Schedule Curves Card */}
            <Paper
              variant="outlined"
              style={{
                padding: spacingToPx(2),
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: spacingToPx(2),
                }}
              >
                <Typography
                  variant="subtitle2"
                  className="flex items-center gap-2"
                >
                  <TimelineIcon fontSize="small" /> Schedule Curves
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddCurve}
                  variant="outlined"
                  size="small"
                  disabled={!tag}
                >
                  Add Curve
                </Button>
              </div>

              {Object.keys(schedules).length > 0 && (
                <>
                  {/* Tabs for different curves */}
                  <div
                    style={{
                      borderBottom: `1px solid hsl(var(--border))`,
                      marginBottom: spacingToPx(2),
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Tabs
                      value={currentTab}
                      onChange={handleTabChange}
                      variant="standard"
                      style={{
                        minHeight: 36,
                      }}
                    >
                      {Object.keys(schedules).map((curveName, index) => (
                        <Tab
                          key={curveName}
                          label={curveName}
                          data-tab-index={index}
                          style={{
                            textTransform: "none",
                            minWidth: 120,
                            minHeight: 36,
                            fontSize: "0.875rem",
                            fontWeight: currentTab === index ? 600 : 500,
                          }}
                        />
                      ))}
                    </Tabs>
                  </div>

                  {/* Current curve controls */}
                  {(() => {
                    const curveNames = Object.keys(schedules);
                    const currentCurveName =
                      curveNames[currentTab] || curveNames[0];
                    const currentSchedule = schedules[currentCurveName] || [];

                    return (
                      <div>
                        {/* Curve management controls */}
                        <div
                          style={{
                            display: "flex",
                            gap: spacingToPx(2),
                            marginBottom: spacingToPx(2),
                            alignItems: "flex-start",
                          }}
                        >
                          <TextField
                            label="Curve Name"
                            value={editingCurveName}
                            onChange={(e) =>
                              setEditingCurveName(e.target.value)
                            }
                            onBlur={(e) =>
                              handleCurveNameChange(
                                currentCurveName,
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleCurveNameChange(
                                  currentCurveName,
                                  e.target.value
                                );
                                e.target.blur();
                              }
                            }}
                            size="small"
                            className="flex-1"
                          />
                          {/* Delete button for current curve */}
                          {Object.keys(schedules).length > 1 && (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleDeleteCurve(currentCurveName)
                              }
                              size="small"
                              startIcon={<DeleteIcon />}
                              style={{
                                marginTop: spacingToPx(3.5), // Align with input field (label height ~24px + margin-bottom ~8px = 32px, but button is smaller so adjust)
                              }}
                            >
                              Delete Curve
                            </Button>
                          )}
                        </div>

                        {/* Schedule table for current curve */}
                        <Table
                          data={currentSchedule}
                          meta={{
                            time: {
                              label: "Time",
                              dataType: "Time",
                            },
                            value: {
                              label: "Value",
                              dataType:
                                tag === "temperature"
                                  ? "Temperature"
                                  : tag === "temperatureDifference"
                                  ? "TemperatureDiff"
                                  : "Number",
                              min:
                                tag === "brightness"
                                  ? 0
                                  : tag === "colorHue"
                                  ? 0
                                  : undefined,
                              max:
                                tag === "brightness"
                                  ? 100
                                  : tag === "colorHue"
                                  ? 360
                                  : undefined,
                            },
                            transition: {
                              label: "Transition",
                              dataType: "FixedSelect",
                              enums: TRANSITION_OPTIONS.map((opt) => opt.value),
                              enumLabels: TRANSITION_OPTIONS.map(
                                (opt) => opt.label
                              ),
                              defaultValue: "l",
                            },
                          }}
                          columns={[
                            { field: "time", label: "Time", dataType: "Time" },
                            {
                              field: "value",
                              label: "Value",
                              dataType:
                                tag === "temperature"
                                  ? "Temperature"
                                  : tag === "temperatureDifference"
                                  ? "TemperatureDiff"
                                  : "Number",
                            },
                            {
                              field: "transition",
                              label: "Transition",
                              dataType: "FixedSelect",
                            },
                          ]}
                          onChange={handleScheduleChange}
                          allowAdd={true}
                          allowDelete={true}
                          minRows={1}
                          bgcolor={"transparent"}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          className="block"
                          style={{ marginTop: spacingToPx(1) }}
                        >
                          System will gradually transition the values between
                          the scheduled time points using the specified
                          transition type. The active curve determines which
                          schedule is currently used. For example, if schedule
                          items, 07:00 - 50 - Accelerate, 08:00 - 100 - Steady,
                          the values will be 50 at 07:00, then transition
                          acceleratingly towards 100 during 07:00 and 08:00.
                        </Typography>
                      </div>
                    );
                  })()}
                </>
              )}
            </Paper>
          </div>
        </div>
      </DialogContent>

      <DialogActions
        className="gap-2"
        style={{
          paddingTop: spacingToPx(3),
          paddingBottom: spacingToPx(2),
          paddingLeft: spacingToPx(2),
          paddingRight: spacingToPx(2),
        }}
      >
        {!isCreate && (
          <Button
            onClick={handleDeleteClick}
            color="error"
            variant="outlined"
            className="mr-auto"
          >
            Delete
          </Button>
        )}
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            loading ||
            !name.trim() ||
            !tag ||
            !selectedLocation ||
            Object.keys(schedules).length === 0 ||
            !activeCurve
          }
        >
          {isCreate ? "Create" : "Save"}
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <CircadianDeleteDialog
        open={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        widgetName={widget?.name}
      />
    </Dialog>
  );
};

export default CircadianEditDialog;
